import { useEffect, useState } from 'react';
import type { Nullable } from 'corgi-types';
import type { ReclamationSolutionList } from 'graphql-ts-types';

import { executeQuery } from 'common/gql';
import type {
  SolutionsQueryQuery,
  SolutionsQueryQueryVariables,
} from 'common/gql/generated/schema';
import { useGQLLocale } from 'common/hooks/locale';
import type { DispatchState } from 'common/types/React';
import solutionsQuery from '../gql/query/solutions.gql';
import type { StepSubmitHandler } from '../types/stepSubmitHandler';
import { dispatchSubmitStepEvent } from '../utils/tracking';
import type { SolutionsTrackingEventData } from '../utils/tracking';
import type { DrawerAlert } from './alert';
import { useFetchStepContent, useObserveAlert } from './index';
import { getUpdateReclamationVariables, updateReclamation } from './update-reclamation';

type UseSolutions = (options: { reclamationId: string; itemId: string; alert: DrawerAlert }) => {
  solutionId: Nullable<string>;
  solutionsContent: Nullable<ReclamationSolutionList>;
  internalAlert: DrawerAlert;
  setSolutionId: DispatchState<Nullable<string>>;
};

interface UseSolutionAssignerOptions {
  reclamationId: string;
  itemId: string;
  solutionId: Nullable<string>;
  trackingEventData: SolutionsTrackingEventData;
  setIsNextDisabled: (value: boolean) => void;
  setOnSubmit: DispatchState<StepSubmitHandler>;
}

type UseSolutionAssigner = (options: UseSolutionAssignerOptions) => [boolean];

const fetchSolutionsStepContent = async (reclamationId: string, itemId: string) => {
  const variables = { reclamationId, itemId };
  const { reclamationItem, content } = await executeQuery<
    SolutionsQueryQuery,
    SolutionsQueryQueryVariables
  >({
    query: solutionsQuery,
    variables,
    useApq: true,
  });

  const solution = reclamationItem?.solution;

  return { currentId: solution?.id ?? null, content };
};

export const useSolutions: UseSolutions = ({ reclamationId, itemId, alert }) => {
  const [internalAlert, setInternalAlert] = useState(alert);

  const {
    content: solutionsContent,
    id: solutionId,
    setId: setSolutionId,
  } = useFetchStepContent<ReclamationSolutionList>({
    reclamationId,
    itemId,
    fetcher: fetchSolutionsStepContent,
    setAlert: setInternalAlert,
  });

  useObserveAlert(alert, setInternalAlert);

  return { solutionId, solutionsContent, internalAlert, setSolutionId };
};

export const useSolutionAssigner: UseSolutionAssigner = ({
  reclamationId,
  itemId,
  solutionId,
  trackingEventData,
  setIsNextDisabled,
  setOnSubmit,
}) => {
  const locale = useGQLLocale();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!solutionId) {
      setIsNextDisabled(true);

      return;
    }

    let isAlive = true;

    setIsNextDisabled(false);
    setOnSubmit(() => async () => {
      if (isAlive) {
        setIsFetching(true);
      }

      setIsNextDisabled(true);

      try {
        await updateReclamation(
          getUpdateReclamationVariables({
            reclamationId,
            itemId,
            solution: solutionId,
            locale,
          })
        );

        dispatchSubmitStepEvent({ ...trackingEventData, label: solutionId });
      } finally {
        if (isAlive) {
          setIsFetching(false);
        }
      }
    });

    return () => {
      isAlive = false;
    };
  }, [reclamationId, itemId, solutionId, setIsNextDisabled, setOnSubmit]);

  return [isFetching];
};
